import { Link, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiClientError } from "@/lib/api-client";
import { useMdfRequestDetail } from "./use-mdf-request-detail";
import { MdfDraftEditView } from "./mdf-draft-edit-view";
import { MdfReadOnlyView } from "./mdf-read-only-view";

// /portal/mdf/:id — status-aware detail page.
//
// Branches on request.status: 'draft' → editable runtime view; any other
// status → read-only view with timeline + comments + attachments. The
// runtime view internally re-fetches /portal/mdf/drafts/:id for the
// allocation-balance shape that the form needs (different from the request
// detail shape). Two fetches for the narrow draft case; single fetch for
// every read-only case (the dominant path post-submit).

export function MdfDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const detail = useMdfRequestDetail(id);

  if (detail.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    const notFound =
      detail.error instanceof ApiClientError && detail.error.status === 404;
    return (
      <div className="mx-auto max-w-2xl rounded-xl border bg-card p-8 text-center">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          {notFound ? "Request not found" : "Couldn’t load this request"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {notFound
            ? "It may have been removed, or you may not have access. Browse your other requests instead."
            : "Please refresh the page. If the problem persists, contact support."}
        </p>
        <Link
          to="/portal/mdf"
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to marketing funds
        </Link>
      </div>
    );
  }

  const status = detail.data.request.status;

  if (status === "draft") {
    return <MdfDraftEditView requestId={id} />;
  }
  return <MdfReadOnlyView detail={detail.data} />;
}
